import { declined, RuleError, type Declined } from './ruleError';
import { usePartLabel } from './partLabel';
import { useAssemblySlots } from './assemblySlots';
import { useLanguage } from '../../contexts/LanguageContext';
import { useMemo } from 'react';
import type { Vec3 } from './assemblyPose';
import { useAssemblySteps } from './categoryBands';
import { fitAxisOf, useHoleFit, type HoleFit } from './holeFit';
import { useMeshOf } from './holeGroups';
import { keptHand, productMiddleX } from './holeLines';
import { jointGap } from './jointMoves';
import { loadDeps } from './meshScene';
import { builtBoundsOf } from './openaiBlenderApi';
import { faceNormal } from './partFaces';
import { isHardwareOrTool, isLeftRight, usePartsRun } from './partsRun';
import { pieceMiddles } from './pieceMiddle';
import { fastenerKind, type StepHole } from './stepHoles';
import { useStepHoles } from './stepHolesRun';
import { pieceContents, pieceItemId, useStepsRun } from './stepsRun';
import { pieceOf, type Job3D } from './threeDApi';

// A COMPONENT PART SET AGAINST ANOTHER, PLACED AS THEIR JOINT HAS THEM.
//
// "Position the side rail against the rear frame, aligning the holes" brings no hardware, so no
// hole is found AT it and the hole rule has nothing to place it by (see fromHole). But the
// joint it prepares already says all of it: both parts stand where the product built them — which
// is where the joint view draws them and where the hardware's lines were run through them (see
// holeLines) — and the hardware that will join them has a line, which is the way one part comes
// onto the other. The model was asked this off a diagram and put the rail 5 cm off.
//
// FOR A COMPONENT PART OF THE PARTS LIST — or a piece made earlier that holds one, the rail with its
// barrel nut in, placed by that rail — ADDED TO SOMETHING A LINE OF HARDWARE JOINS IT TO: another
// part of the list, or the piece the sub-steps before have made — the second rail against a frame
// that already carries the first. The piece's parts stand where the product built them too, so
// the rail's place among them is the same subtraction; what changes is what it is subtracted
// from, the piece's own middle (see pieceMiddle).
//
// EITHER HAND OF A PAIR. A pair is one file, the kept hand's, and the other is that file
// reflected across the product's middle: its place is the kept one's reflected, and it is drawn
// reflected (`mirrored`, see ScenePart.mirror). Which hand a sub-step brings is said by the line
// that will join it — the hole it makes in the pair is in one unit, and says which.
//
// Not for a part RECEIVED that is the other hand on its own, with no piece around it: nothing is
// drawn reflected but the part that moves.

export type JointPlacement = {
  // The product run the parts were cut from.
  of: number;
  // Where the moving part's centre rests, from the middle of what it is placed on, in
  // millimetres. Worked out on the press: a piece's middle is asked for (see pieceMiddle).
  // `modelOf`: the phase's own drawable parts, pieces included.
  seat: (modelOf: Map<number, Job3D>) => Promise<Vec3>;
  // Which way the moving part starts out, along the line of the hardware that will join them.
  axis: Vec3;
  // THE MOVING PART IS THE OTHER HAND of its pair — the kept file, reflected.
  mirrored: boolean;
  // That line: its entry hole's id, and its hardware's model — whose length says how far out.
  line: string;
  fit: HoleFit;
};

const centre = (b: { min: number[]; max: number[] }): Vec3 =>
  [(b.min[0] + b.max[0]) / 2, (b.min[1] + b.max[1]) / 2, (b.min[2] + b.max[2]) / 2];

// Null where the sub-step is no joint's to place: no component part moves in it. Declined, and
// why, where it is one and the joint cannot answer (see the head of this file).
export function useJointPlacement(docId: string, slotKey: string | null | undefined): JointPlacement | Declined | null {
  const { steps } = useAssemblySteps(docId);
  const { subSteps } = useStepsRun(docId);
  const { items } = usePartsRun(docId);
  const { byStep } = useStepHoles(docId);
  const meshOf = useMeshOf(docId);
  const fitOf = useHoleFit(docId);
  const label = usePartLabel(docId);
  const slots = useAssemblySlots(docId);
  const leaderOf = (key: string) => slots.find((s) => s.key === key)?.repeat?.leader ?? key;
  const { t } = useLanguage();
  return useMemo(() => {
    const [category, letter] = (slotKey ?? '').split('#');
    const at = steps.findIndex((s) => s.category === category);
    const subs = subSteps[category] ?? [];
    const here = subs.findIndex((s) => s.letter === letter);
    if (at < 0 || here < 0) return null;
    // Bar hardware prepared in the hand (see fromHole): it is not an end of the fitting.
    const parts = (subs[here].parts ?? []).filter((p) => p.role !== 'prepared');
    const component = (id: string | undefined) => (id != null && items[Number(id)] && !isHardwareOrTool(items[Number(id)]) ? id : null);
    const received = parts.find((p) => p.role === 'receiving')?.id;
    const added = parts.find((p) => p.role === 'added')?.id;
    // WHAT MOVES: a component part, or a piece made earlier that holds exactly one — the rail with
    // its barrel nut already in. The piece is placed by that component, which the joint knows.
    const movingPiece = added != null && !items[Number(added)] ? pieceItemId(added) : null;
    const contents = pieceContents(steps, subSteps);
    const carried = movingPiece != null ? (contents.get(added!) ?? []).filter((id) => component(id) != null) : [];
    const moving = movingPiece != null ? (carried.length === 1 ? carried[0] : null) : component(added);
    // NOT A JOINT'S: what moves is hardware, or a piece of nothing but hardware (the hole's).
    if (added != null && !moving && carried.length === 0) return null;
    if (parts.length !== 2 || received == null || added == null) return declined('notice.rule.noRoles');
    if (!moving) return declined('notice.rule.joint.pieceMany', { part: label(added) });
    // WHAT IT IS ADDED TO, as the component parts in it: the part itself, or everything a piece
    // made earlier holds.
    const piece = items[Number(received)] ? null : pieceItemId(received);
    const held = piece == null
      ? [component(received)].filter((id): id is string => id != null)
      : (contents.get(received) ?? []).filter((id) => component(id) != null);
    if (!held.length) return declined('notice.rule.joint.nothingHeld', { onto: label(received) });
    const mJob = meshOf(moving);
    const mBox = builtBoundsOf(mJob);
    const mOf = pieceOf(mJob);
    if (!mBox || mOf?.from !== 'product') return declined('notice.rule.joint.notFromProduct', { part: label(moving) });
    // THE LINE OF HARDWARE THAT WILL JOIN THE TWO: an entry hole in one, a hole of the same line in
    // the other — the holes are connected, and that is the whole of it. Entry holes only: each line
    // is filed whole under the entry it is driven through.
    //
    // NOT BY WHEN EACH HOLE IS DRIVEN. It was "the first line from this sub-step on", read off each
    // hole's sub-step (`at`), and that number moves with every re-run of the steps: on prod it was
    // gone from every hole and the rail found no line, and offline it put the right-hand line first
    // and the rail came in mirrored. Which line it is was never a question of time.
    //
    // A PAIR IS TWO UNITS AND TWO LINES, one model for both, so the rail meets the frame by the
    // left-hand line and by the right-hand one. The sub-step says which unit it brings (the side
    // Phase 4 gives a pair's entry, see SubStep parts), and each hole of a pair's line is in one
    // unit (StepHole.side): the line is the one whose hole in the moving part is on that side, and
    // likewise for a received part that names its side. Timing is a tie-break and nothing more.
    //
    // A PAIR'S OTHER UNIT MAY ALREADY BE IN THE PIECE, under the same part id as the one being
    // added: a hole in that part is taken for the unit being added, and the side is what keeps the
    // line that joined the first unit out.
    const sideOf = (role: 'added' | 'receiving') => parts.find((p) => p.role === role)?.side;
    const onMoving = (h: StepHole) => h.part === moving;
    const onHeld = (h: StepHole) => h.part !== moving && held.includes(h.part);
    const across = (entry: StepHole, o: StepHole) => o !== entry && o.line === entry.id && o.via === entry.via
      && (onMoving(entry) ? onHeld(o) : onMoving(o));
    const joins = (entry: StepHole, holes: StepHole[]) => entry.via != null
      && (entry.line == null || entry.line === entry.id)
      && (onMoving(entry) || onHeld(entry))
      && holes.some((o) => across(entry, o));
    const sided = (h: StepHole, side: string | undefined) => !side || !h.side || h.side === side;
    let found: { entry: StepHole; next: StepHole } | null = null;
    for (let i = at; i < steps.length && !found; i += 1) {
      const holes = byStep[steps[i].category]?.holes ?? [];
      const order = (h: StepHole) => (subSteps[steps[i].category] ?? []).findIndex((s) => s.letter === h.at);
      // From this sub-step on first, where the holes are timed; then any; then by hole id.
      const later = (h: StepHole) => (i > at || order(h) > here ? 0 : 1);
      const lines = holes
        .filter((h) => joins(h, holes))
        .map((entry) => ({ entry, next: holes.find((o) => across(entry, o))! }))
        .filter(({ entry, next }) => {
          const [m, t] = onMoving(entry) ? [entry, next] : [next, entry];
          return sided(m, sideOf('added')) && sided(t, sideOf('receiving'));
        })
        .sort((a, b) => later(a.entry) - later(b.entry)
          || (order(a.entry) < 0 ? 1e6 : order(a.entry)) - (order(b.entry) < 0 ? 1e6 : order(b.entry))
          || a.entry.id.localeCompare(b.entry.id, undefined, { numeric: true }));
      if (lines.length) found = lines[0];
    }
    if (!found) {
      const side = sideOf('added');
      return declined('notice.rule.joint.noLine', {
        part: label(moving), onto: label(received), side: side ? t(`notice.rule.joint.onSide.${side}`) : '',
      });
    }
    const { entry, next } = found;
    const kind = fastenerKind(items[Number(entry.via)]);
    const fit = fitOf(entry);
    if (!kind || !fit) return declined('notice.rule.joint.lineNoModel', { line: entry.id, part: label(entry.via!) });
    // The part of what it is added to that the line runs through: cut from the same product.
    const through = onMoving(entry) ? next : entry;
    const tBox = builtBoundsOf(meshOf(through.part));
    const tOf = pieceOf(meshOf(through.part));
    if (!tBox || tOf?.from !== 'product' || tOf.of !== mOf.of) {
      return declined('notice.rule.joint.notSameRun', { part: label(moving), onto: label(through.part) });
    }
    // WHICH HAND EACH HOLE IS IN, against the kept file's: the other is that file reflected.
    const mx = productMiddleX(items, meshOf);
    const otherHand = (h: StepHole, box: NonNullable<typeof mBox>) => {
      if (!isLeftRight(items[Number(h.part)]) || !h.side) return false;
      return mx == null ? null : keptHand(box, mx) !== h.side;
    };
    const mirrored = otherHand(onMoving(entry) ? entry : next, mBox);
    const heldOther = otherHand(through, tBox);
    // No middle to reflect across; or a part received on its own as the other hand, which nothing
    // draws reflected — in a piece it stands wherever the sub-step that added it put it.
    if (mirrored == null || heldOther == null) return declined('notice.rule.joint.noMiddle');
    if (heldOther && piece == null) return declined('notice.rule.joint.otherHandAlone', { onto: label(received) });
    // WHERE THE NEXT PART LIES FROM THE ENTRY PART, as the joint view comes apart (see jointSpread):
    // out of the face for a dowel, through the part for a screw. A hole in the other hand is said
    // in the kept file's words, so its face looks the other way across the product.
    const said = faceNormal(entry.face);
    const n: Vec3 = (onMoving(entry) ? mirrored : heldOther) ? [-said[0], said[1], said[2]] : said;
    const d: Vec3 = kind === 'seated' ? n : [-n[0], -n[1], -n[2]];
    const middleOf = pieceMiddles({ docId, steps, subSteps, items, meshOf, label, leaderOf });
    return {
      of: mOf.of,
      seat: async (modelOf) => {
        // A piece's middle, where its component stands as the product built it (see pieceMiddle).
        const kept = movingPiece != null ? await middleOf(added, modelOf) : centre(mBox);
        const a: Vec3 = mirrored ? [2 * mx! - kept[0], kept[1], kept[2]] : kept;
        const b = await middleOf(received, modelOf);
        return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
      },
      axis: onHeld(entry) ? d : [-d[0], -d[1], -d[2]],
      mirrored,
      line: entry.id,
      fit,
    };
  }, [docId, slotKey, steps, subSteps, items, byStep, meshOf, fitOf, label, t, slots]);
}

// HOW FAR OUT IT STARTS: as far apart as the joint view stands the two, exploded (see jointGap) —
// the hardware's length, off its model. A model that will not load is an error, not a guess.
export async function travelFromJoint(p: JointPlacement): Promise<number> {
  const { THREE, GLTFLoader } = await loadDeps();
  const axis = await fitAxisOf(THREE, new GLTFLoader(), p.fit).catch(() => null);
  if (!axis) throw new RuleError('notice.rule.modelNoLength', { job: p.fit.job.id });
  return Math.round(jointGap(axis.len) * 10) / 10;
}
